import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import "../../styles/Torrent.css";
import {useCookies} from "react-cookie";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {Navigate, useNavigate} from "react-router-dom";

export default function Torrent({ dark, setDark, sidebarOpen, setSidebarOpen }) {

    const navigate = useNavigate();

    // --- DATA ---
    const [torrents, setTorrents] = useState([]);

    // --- PAGINATION ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 11;

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const [cookies, setCookie, removeCookie] = useCookies(['user']);
    const [user, setUser] = useState(null);
    const [nextUpdate, setNextUpdate] = useState(0);
    const [isUpdating, setIsUpdating] = useState(false);
    const [now, setNow] = useState(Date.now());

    // --- SEARCH ---
    const [search, setSearch] = useState("");
    const [showRequestedOnly, setShowRequestedOnly] = useState(false);

    // --- Rechart ---
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const decodeToken = (token) => {
        try {
            const payload = token.split(".")[1];
            return JSON.parse(atob(payload));
        } catch {
            return null;
        }
    };

    useEffect(() => {
        if (!cookies.token) return;

        const decoded = decodeToken(cookies.token);
        setUser(decoded); // contient { id, username, role, ... }
    }, [cookies.token]);



    const filteredTorrents = torrents.filter(t => {
        const matchesSearch = (t.title || "").toLowerCase().includes(search.toLowerCase()) ||
            (t.web || "").toLowerCase().includes(search.toLowerCase());

        if (!matchesSearch) return false;
        if (showRequestedOnly && !t.requested) return false;
        return true;
    });

    const chartData = Array.from({ length: 12 }).map((_, i) => ({
        month: new Date(0, i).toLocaleString("fr-FR", { month: "short" }),
        count: 0
    }));

    filteredTorrents.forEach(t => {
        if (!t.created_at) return;

        const d = new Date(t.created_at);
        if (d.getFullYear() !== selectedYear) return;

        const monthIndex = d.getMonth();
        chartData[monthIndex].count++;
    });


    const totalPages = Math.ceil(filteredTorrents.length / itemsPerPage);
    const currentTorrents = filteredTorrents.slice(indexOfFirstItem, indexOfLastItem);

    const toggleTheme = () => setDark(!dark);


    const getPaginationButtons = () => {
        const pages = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 4) {
                pages.push(1, 2, 3, 4, 5, "...", totalPages);
            } else if (currentPage >= totalPages - 3) {
                pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
            }
        }

        return pages;
    };

    const getSiteColor = (site) => {
        if (!site) return "badge-default";

        const s = site.toLowerCase();

        if (s.includes("skidrow")) return "badge-blue";
        if (s.includes("c411") || s.includes("ygg")) return "badge-purple";

        return "badge-default";
    };


    // --- TOASTS ---
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [toastProgress, setToastProgress] = useState(100);

    const showSuccess = (msg) => {
        setSuccessMessage(msg);
        setToastProgress(100);

        let width = 100;
        const interval = setInterval(() => {
            width -= 1;
            setToastProgress(width);
            if (width <= 0) {
                clearInterval(interval);
                setSuccessMessage("");
            }
        }, 30);
    };

    const showError = (msg) => {
        setErrorMessage(msg);
        setToastProgress(100);

        let width = 100;
        const interval = setInterval(() => {
            width -= 1;
            setToastProgress(width);
            if (width <= 0) {
                clearInterval(interval);
                setErrorMessage("");
            }
        }, 30);
    };

    // --- DRAWER ADD ---
    const [openAddDrawer, setOpenAddDrawer] = useState(false);
    const [closingDrawer, setClosingDrawer] = useState(false);

    const [newTorrent, setNewTorrent] = useState({
        title: "",
        lien: "",
        web: "",
        created_at: "",
    });

    const openDrawer = () => {
        if (user?.role !== "Admin") return;
        setClosingDrawer(false);
        setOpenAddDrawer(true);
    };

    const closeDrawer = () => {
        setClosingDrawer(true);
        setTimeout(() => {
            setOpenAddDrawer(false);
            setClosingDrawer(false);
        }, 300);
    };

    // --- DELETE CONFIRM ---
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [closingConfirm, setClosingConfirm] = useState(false);

    const openConfirm = (id) => {
        if (user?.role !== "Admin") return;
        setClosingConfirm(false);
        setDeleteConfirm(id);
    };

    const closeConfirm = () => {
        setClosingConfirm(true);
        setTimeout(() => {
            setDeleteConfirm(null);
            setClosingConfirm(false);
        }, 300);
    };

    // --- API CALLS ---
    const fetchTorrents = async () => {
        try {
            const res = await fetch("https://api.industriegaming.fr/api/script/torrents/getall", {
                headers: {
                    Authorization: `Bearer ${cookies.token}`
                }
            });

            if (!res.ok) {
                if (res.status === 401) {
                    removeCookie("token", { path: "/" });
                    navigate("/");
                    return null;
                }

                const txt = await res.text().catch(() => "");
                throw new Error(`fetchTorrents HTTP ${res.status} ${txt}`);
            }

            const data = await res.json();
            setTorrents(data);
            return data;
        } catch (err) {
            console.error("fetchTorrents error:", err);
            showError("Impossible de charger les torrents");
            return null;
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);

        const minsStr = mins.toString().padStart(2, "0");
        const secsStr = secs.toString().padStart(2, "0");

        return `${minsStr}m${secsStr}s`;
    };


    const updateTorrents = async () => {
        try {
            const res = await fetch("https://api.industriegaming.fr/api/bot/nextupdate", {
                headers: {
                    Authorization: `Bearer ${cookies.token}`
                }
            });

            if (!res.ok) {
                if (res.status === 401) {
                    removeCookie("token", { path: "/" });
                    navigate("/");
                    return null;
                }

                const txt = await res.text().catch(() => "");
                throw new Error(`updateTorrents HTTP ${res.status} ${txt}`);
            }

            const data = await res.json();

            // Parse the server-provided next_update string (expected format: "DD/MM/YYYY, HH:MM:SS")
            // into a safe timestamp to compute remaining seconds locally.
            let timestamp = null;
            if (data.next_update) {
                try {
                    const parts = data.next_update.split(",").map(s => s.trim());
                    const datePart = parts[0];
                    const timePart = parts[1] || "00:00:00";
                    const [day, month, year] = datePart.split("/").map(Number);
                    const [hour, minute, second] = timePart.split(":").map(Number);
                    timestamp = new Date(year, month - 1, day, hour || 0, minute || 0, second || 0).getTime();
                } catch (err) {
                    // Fallback: compute timestamp from seconds_remaining
                    timestamp = Date.now() + Math.round((data.seconds_remaining || 0) * 1000);
                }
            } else {
                timestamp = Date.now() + Math.round((data.seconds_remaining || 0) * 1000);
            }

            setNextUpdate({ ...data, timestamp });
            // console.log("updateTorrents result:", data);
            return data;
        } catch (err) {
            console.error("updateTorrents error:", err);
            showError("Impossible de charger la prochaine mise à jour");
            return null;
        }
    };

    const addTorrent = async () => {
        try {
            const res = await fetch("http://ton-backend.com/torrents", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`
                },
                body: JSON.stringify(newTorrent)
            });

            if (!res.ok) {
                showError("Erreur lors de l'ajout");
                return;
            }

            fetchTorrents();
            showSuccess("Torrent ajouté !");
            closeDrawer();
            setNewTorrent({ title: "", lien: "", web: "", created_at: "" });

        } catch (err) {
            showError("Une erreur est survenue");
        }
    };

    const deleteTorrent = async () => {
        try {
            const res = await fetch(`http://ton-backend.com/torrents/${deleteConfirm}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`
                }
            });

            if (!res.ok) {
                showError("Impossible de supprimer");
                return;
            }

            fetchTorrents();
            showSuccess("Torrent supprimé !");
        } catch (err) {
            showError("Une erreur est survenue");
        }
    };

    const toggleRequested = async (id, currentRequested) => {
        try {
            const newValue = currentRequested ? 0 : 1;
            const res = await fetch(`https://api.industriegaming.fr/api/script/torrents/request/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${cookies.token}`
                },
                body: JSON.stringify({ requested: newValue })
            });

            if (!res.ok) {
                const txt = await res.text().catch(() => "");
                throw new Error(`toggleRequested HTTP ${res.status} ${txt}`);
            }

            await fetchTorrents();
            showSuccess(newValue ? "Torrent marqué comme demandé" : "Torrent marqué comme non demandé");
        } catch (err) {
            console.error("toggleRequested error:", err);
            showError("Impossible de mettre à jour le torrent");
        }
    };

    useEffect(() => {
        fetchTorrents();
        updateTorrents();
    }, []);

    useEffect(() => {
        if (!nextUpdate) return;

        const interval = setInterval(() => {
            setNow(Date.now());
            const remaining = Math.max(0, Math.round((nextUpdate.timestamp - Date.now()) / 1000));


            if (remaining <= 0) {

                if (isUpdating) return; // éviter les appels multiples si déjà en cours
                setIsUpdating(true);
                setTimeout(async () => {
                    const updated = await updateTorrents(); // récupère le nouveau timer
                    if (updated) {
                        await fetchTorrents();  // recharge la liste seulement si update ok
                    } else {
                        console.warn("updateTorrents failed, skipping fetchTorrents");
                    }
                    setIsUpdating(false);
                }, 30000);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [nextUpdate]);




    return (
        <div className={`dashboard ${dark ? "dark" : ""}`}>
            <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="main">
                <Topbar onToggleSidebar={() => setSidebarOpen(true)} onToggleTheme={toggleTheme} dark={dark}/>

                {/* TOASTS — DOIVENT ÊTRE ICI */}
                {successMessage && (
                    <div className="toast-success">
                        {successMessage}
                        <div className="toast-progress" style={{ width: `${toastProgress}%` }}></div>
                    </div>
                )}

                {errorMessage && (
                    <div className="toast-error">
                        {errorMessage}
                        <div className="toast-progress" style={{ width: `${toastProgress}%` }}></div>
                    </div>
                )}

                <div className="users-page">
                    {openAddDrawer && (
                        <div
                            className={`drawer-overlay ${closingDrawer ? "closing" : ""}`}
                            onClick={closeDrawer}
                        >
                            <div
                                className={`drawer ${closingDrawer ? "closing" : ""}`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <h2>Ajouter un torrent</h2>

                                <label>Titre</label>
                                <input
                                    type="text"
                                    value={newTorrent.title}
                                    onChange={(e) =>
                                        setNewTorrent({...newTorrent, title: e.target.value})
                                    }
                                />

                                <label>Lien</label>
                                <input
                                    type="text"
                                    value={newTorrent.link}
                                    onChange={(e) =>
                                        setNewTorrent({...newTorrent, link: e.target.value})
                                    }
                                />

                                <label>Site</label>
                                <input
                                    type="text"
                                    value={newTorrent.site}
                                    onChange={(e) =>
                                        setNewTorrent({...newTorrent, site: e.target.value})
                                    }
                                />

                                <button className="save-user-btn" onClick={addTorrent}>
                                    Ajouter
                                </button>
                            </div>
                        </div>
                    )}


                    {/* HEADER IDENTIQUE */}
                    <div className="users-header">
                        <div className="users-header-logo">
                            <h1>Torrents</h1>
                            <div className="search-wrapper">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Rechercher un torrent..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                {search && (
                                    <button
                                        type="button"
                                        className="search-clear"
                                        onClick={() => setSearch("")}
                                        aria-label="Clear search"
                                    >
                                        ✖
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                className={`requested-filter ${showRequestedOnly ? 'active' : ''}`}
                                onClick={() => setShowRequestedOnly(prev => !prev)}
                                title="Afficher uniquement les torrents demandés"
                            >
                                Demandés
                            </button>
                        </div>
                        <div className="users-header-logo">
                            {/*<div className="results-count">*/}
                            <div className={isUpdating ? 'results-count red-timer' : 'results-count'}>
                                    {(() => {
                                        if (isUpdating) return "Mise à jour en cours... Merci de refraîchir la page dans quelques secondes.";

                                        const remainingSeconds = (nextUpdate && nextUpdate.timestamp)
                                            ? Math.max(0, Math.round((nextUpdate.timestamp - now) / 1000))
                                            : null;

                                        if (remainingSeconds === null) return "Prochaine mise à jour inconnue";

                                        return `Prochaine mise à jour dans ${formatTime(remainingSeconds)} (${nextUpdate.next_update})`;
                                    })()}
                                </div>

                                <div className="results-count">
                                    {filteredTorrents.length} torrents trouvés
                                </div>
                                {user?.role === "Admin" && (
                                    <button className="add-user-btn" onClick={openDrawer}>
                                        + Ajouter un torrent
                                    </button>
                                )}
                            </div>
                        </div>


                        {/* WRAPPER IDENTIQUE */}
                    <div className="users-table-wrapper">
                    <table className="users-table torrent-log">
                            <thead>
                            <tr>
                                <th className="torrent-title">Titre</th>
                                <th>Site</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                            </thead>

                            <tbody>
                            {currentTorrents.map((t) => (
                                    <tr key={t.id} className={t.requested ? 'requested-row' : ''}>
                                        <td className="torrent-title">
                                            {t.requested && (
                                                <span className="requested-badge title-badge">Demandé</span>
                                            )}
                                            <a href={t.lien} target="_blank" rel="noopener noreferrer"
                                               className="torrent-link">
                                                {t.title}
                                            </a>
                                        </td>
                                    {/*<td>{t.web}</td>*/}
                                    <td>
                                        <span className={`site-badge ${getSiteColor(t.web)}`}>
                                            {t.web}
                                        </span>
                                    </td>

                                    <td>
                                        {new Date(t.created_at).toLocaleDateString("fr-FR")}{" - "}
                                        {new Date(t.created_at).toLocaleTimeString("fr-FR", {
                                            hour: "2-digit",
                                            minute: "2-digit"
                                        })}
                                    </td>
                                    <td className="actions">
                                        {user?.role === "Admin" && (
                                            <>
                                                <button
                                                    className="request-toggle-btn"
                                                    onClick={() => toggleRequested(t.id, t.requested)}
                                                    title={t.requested ? "Marquer comme non demandé" : "Marquer comme demandé"}
                                                >
                                                    {t.requested ? '🔵' : '⚪'}
                                                </button>

                                                <button
                                                    className="delete-btn"
                                                    onClick={() => openConfirm(t.id)}
                                                >
                                                    🗑️
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {filteredTorrents.length === 0 && (
                                <tr className="no-results-row">
                                    <td colSpan="4" className="no-results">
                                        Aucun torrent trouvé
                                    </td>
                                </tr>
                            )}

                            {/* Lignes vides pour garder la hauteur comme Users */}
                            {filteredTorrents.length > 0 &&
                                Array.from({length: itemsPerPage - currentTorrents.length}).map((_, i) => (
                                    <tr key={`empty-${i}`} className="empty-row">
                                        <td colSpan="4"></td>
                                    </tr>
                                ))
                            }

                            </tbody>

                        </table>

                        {/* PAGINATION IDENTIQUE */}
                        <div className="pagination">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                <i className="fa-solid fa-chevron-left"></i>
                            </button>

                            {getPaginationButtons().map((page, index) =>
                                page === "..." ? (
                                    <span key={index} className="dots">...</span>
                                ) : (
                                    <button
                                        key={index}
                                        className={currentPage === page ? "active" : ""}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                )
                            )}

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>

                    </div>
                    <div className="users-table-wrapper request-chart-wrapper">
                        <div className="chart-header">
                            <h2 className="chart-title">Torrents ajoutés par mois</h2>

                            <select
                                className="year-dropdown"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                            >
                                {Array.from({length: 5}).map((_, i) => {
                                    const year = new Date().getFullYear() - i;
                                    return <option key={year} value={year}>{year}</option>;
                                })}
                            </select>
                        </div>

                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={chartData}>
                                <XAxis dataKey="month" stroke={dark ? "#ffffff" : "#000000"}/>
                                <YAxis stroke={dark ? "#ffffff" : "#000000"}/>
                                <Tooltip
                                    cursor={{fill: "transparent"}}
                                    contentStyle={{
                                        background: "var(--card-bg)",
                                        border: "1px solid var(--border-color)",
                                        color: dark ? "#ffffff" : "#000000"
                                    }}
                                    formatter={(value) => [`${value} torrents`]}
                                    labelFormatter={(label) => `Mois : ${label}`}
                                />
                                <Bar
                                    dataKey="count"
                                    fill={dark ? "#598580" : "#98e3da"}
                                    radius={[6, 6, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>


                </div>
                {deleteConfirm && (
                    <div className="confirm-overlay" onClick={closeConfirm}>
                        <div
                            className={`confirm-box ${closingConfirm ? "closing" : ""}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3>Supprimer ce torrent ?</h3>
                            <p>Cette action est irréversible.</p>

                            <div className="confirm-actions">
                                <button className="cancel-btn" onClick={closeConfirm}>
                                    Annuler
                                </button>

                                <button
                                    className="delete-btn2"
                                    onClick={async () => {
                                        await deleteTorrent();
                                        closeConfirm();
                                    }}
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
